import React, { useState, useEffect, useRef, useContext } from 'react'
import PropTypes from 'prop-types'
import {
  Link,
  Container,
  Button,
  AppBar,
  Tabs,
  Tab,
  Box,
  makeStyles,
  styled
} from '@material-ui/core'
import clsx from 'clsx'
import SearchBox from './SearchBox'
import CurrencyDropdown from './CurrencyDropdown'
import PlansAccordion from './PlansAccordion'
import { LocaleContext } from '../../contexts/LocaleProvider'

const SearchContainer = styled(Container)(() => ({
  padding: 0
}))
const SearchBoxWrap = styled(Box)(() => ({
  position: 'relative'
}))
const WhiteBox = styled(Box)(({ theme }) => ({
  maxWidth: 830,
  padding: theme.spacing(0.5, 0),
  marginLeft: 'auto',
  marginRight: 'auto',
  borderRadius: 4,
  boxShadow: '0 4px 5px 0 rgb(13 49 63 / 5%), 0 1px 5px 0 rgb(13 49 63 / 5%)',
  backgroundColor: theme.colors.white
}))
function TabPanel(props) {
  const { children, value, index, ...other } = props

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
      {...other}
    >
      {value === index && <Box>{children}</Box>}
    </div>
  )
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired
}

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3)
  },
  container: {
    flexGrow: 1
  },
  appbar: {
    maxWidth: 830,
    margin: 'auto',
    marginTop: theme.spacing(6),
    marginBottom: theme.spacing(6),
    backgroundColor: 'transparent'
  },
  tabs: {
    borderBottom: 'none',
    position: 'relative',

    '& > div > div': {
      justifyContent: 'center',
      backgroundColor: 'transparent',
      [theme.breakpoints.down('sm')]: {
        justifyContent: 'flex-start'
      }
    }
  },
  tab: {
    width: (props) =>
      props.regions && props.regions.length !== 0 ? `calc(100%/3)` : `calc(100%/2)`,
    maxWidth: '100%',
    minHeight: theme.spacing(7),
    whiteSpace: 'nowrap',
    fontWeight: 700,
    backgroundColor: theme.colors.grey3,
    margin: '4px 0',
    overflow: 'visible',
    '& span': {
      position: 'relative',
      zIndex: 1
    },
    '&:first-child': {
      borderTopLeftRadius: 4,
      borderBottomLeftRadius: 4,
      '&::before': {
        content: '""',
        position: 'absolute',
        top: '25%',
        right: '0',
        height: '50%',
        width: 1,
        backgroundColor: 'rgba(13, 49, 63, 0.2)'
      }
    },
    '&:last-child': {
      borderTopRightRadius: 4,
      borderBottomRightRadius: 4,
      '&::before': {
        content: '""',
        position: 'absolute',
        top: '25%',
        left: '0',
        height: '50%',
        width: 1,
        backgroundColor: 'rgba(13, 49, 63, 0.2)'
      }
    },
    '&.Mui-selected': {
      color: theme.colors.white,
      position: 'relative',
      '&::after': {
        content: '""',
        display: 'block',
        position: 'absolute',
        backgroundColor: theme.colors.blue1,
        borderRadius: 4,
        color: theme.colors.white,
        top: -3,
        left: 0,
        width: '100%',
        height: '110%',
        zIndex: 0
      }
    }
  },
  searchIcon: {
    position: 'absolute',
    right: theme.spacing(2),
    top: theme.spacing(2)
  },
  regionLink: {
    color: theme.colors.colorTransparent4,
    display: 'inline-block',
    margin: theme.spacing(0, 2),
    marginBottom: theme.spacing(1.2),
    fontSize: '1.25rem',
    fontWeight: 'bold',
    '&:hover': {
      color: theme.colors.blue5
    }
  },
  selectedRegion: {
    borderBottom: '3px solid ' + theme.colors.blue1,
    color: theme.colors.blue5,
    paddingBottom: theme.spacing(1)
  },
  regionalLinks: {
    [theme.breakpoints.up('sm')]: {
      justifyContent: 'center'
    }
  },
  regionalLinkScroll: {
    overflow: 'auto',
    whiteSpace: 'nowrap',
    marginTop: theme.spacing(5),
    marginBottom: theme.spacing(3)
  },
  showMore: {
    marginTop: theme.spacing(5)
  }
}))

const SearchResult = (props) => {
  const [open, setOpen] = useState(true)

  return (
    <PlansAccordion
      isOpen={open || !props.allowOpenClose}
      toggleOpen={() => setOpen(!open)}
      {...props}
    />
  )
}

const PlanGroup = (props) => {
  const [page, setPage] = useState(1)
  const [moreButtonPos, setMoreButtonPos] = useState()
  const plansToShow = props.plans.slice(0, page * 10)
  const classes = useStyles(props)
  const [openPlan, setOpenPlan] = useState(null)
  const accordElem = useRef([])
  const moreButton = useRef(null)

  const toggleOpen = (name) => {
    if (openPlan === name) {
      setOpenPlan(null)
    } else {
      setOpenPlan(name)
    }
  }
  const shouldShowMoreButton = props.plans.length > page * 10
  const nextPage = (e) => {
    e.preventDefault()
    setPage(page + 1)
    window.scrollBy(0, -moreButtonPos)
  }

  useEffect(() => {
    if (moreButton.current) {
      setMoreButtonPos(moreButton.current.getBoundingClientRect().x)
    }

    let selectedPlanName
    if (props.selectedPlan) {
      selectedPlanName = plansToShow.find((x) => x.region === props.selectedPlan)?.name
    }

    if (!selectedPlanName) {
      selectedPlanName = props.lastItemOpen ? plansToShow[plansToShow.length - 1].name : null
    }

    setOpenPlan(selectedPlanName)
  }, [props.selectedPlan])

  return (
    <>
      {plansToShow.map((plan, i, list) => {
        return (
          <PlansAccordion
            ref={(el) => (accordElem.current[i] = el)}
            key={plan.name}
            isOpen={openPlan === plan.name || list.length === 1}
            toggleOpen={(name) => toggleOpen(name, i)}
            allowOpenClose={list.length > 1}
            culture={props.culture}
            selectedCurrency={props.selectedCurrency}
            coverageTitle={props.coverageTitle}
            coverageDescription={props.coverageDescription}
            howMuchDataLink={props.howMuchDataLink}
            dataText={props.dataText}
            modal={props.modal}
            modalTitle={props.modalTitle}
            appStoreImage={props.appStoreImage}
            appStoreUrl={props.appStoreUrl}
            playStoreImage={props.playStoreImage}
            playStoreUrl={props.playStoreUrl}
            planIcon={props.planIcon}
            freePlanEnabled={props.freePlanEnabled}
            freePlanAllowance={props.freePlanAllowance}
            freePlanPrice={props.freePlanPrice}
            freePlanDuration={props.freePlanDuration}
            freePlanUnit={props.freePlanUnit}
            {...plan}
          />
        )
      })}

      {shouldShowMoreButton && (
        <Button ref={moreButton} variant="outlined" onClick={nextPage} className={classes.showMore}>
          {props.showMoreText}
        </Button>
      )}
    </>
  )
}

export default function PlanTabs(props) {
  const classes = useStyles(props)
  const plansRef = useRef(null)

  const [selectedTab, setSelectedTab] = useState(props.defaultTab || 'global')
  const [selectedPlan, setSelectedPlan] = useState(null)

  const [selectedRegion, setSelectedRegion] = useState(
    props.defaultRegion ? props.defaultRegion : null
  )

  const localeContext = useContext(LocaleContext)
  const [selectedCountries, setSelectedCountries] = useState([])

  const globalPlan = props.plans.find((x) => x.footprintType === 'Global')

  let localPlans = props.plans
    .filter((x) => x.footprintType === 'Local')
    .sort((a, b) => a.name?.localeCompare(b.name))

  let regionalPlans
  if (props.regions && props.regions.length !== 0) {
    regionalPlans = props.plans
      .filter((x) => x.footprintType === 'Regional')
      .sort((a, b) => a.name?.localeCompare(b.name))
  }

  let searchPlans = []
  if (selectedCountries && selectedCountries.length > 0) {
    const selectedIsoCountries = selectedCountries.map((country) => {
      return country.iso
    })
    if (selectedIsoCountries.length === 1) {
      let searchLocalPlan = localPlans.find(
        (localPlan) => localPlan.iso === selectedIsoCountries[0]
      )
      if (searchLocalPlan) {
        searchPlans.push(searchLocalPlan)
      }
    }
    if (props.regions && props.regions.length !== 0) {
      regionalPlans.forEach((regionalPlan) => {
        if (
          selectedIsoCountries.every((iso) =>
            regionalPlan.footprintCountries.find((country) => country.iso === iso)
          )
        ) {
          searchPlans.push(regionalPlan)
        }
      })
    }

    searchPlans.push(globalPlan)
  } else if (selectedRegion) {
    localPlans = localPlans.filter((x) => selectedRegion === null || x.region === selectedRegion)
  }

  const getTopCoords = (elem) => {
    if (!elem) return
    var box = elem.getBoundingClientRect()
    var body = document.body
    var docEl = document.documentElement
    var scrollTop = window.pageYOffset || docEl.scrollTop || body.scrollTop
    var clientTop = docEl.clientTop || body.clientTop || 0
    var top = box.top + scrollTop - clientTop
    return Math.round(top)
  }

  useEffect(() => {
    const tabs = ['global', 'regional', 'local']
    let scrollToPlans = false
    if (window.location.hash) {
      const tabTitle = decodeURIComponent(window.location.hash.substring(1)).replace(/-/, ' ')
      if (tabs.includes(tabTitle)) {
        setSelectedTab(tabTitle)
        scrollToPlans = true
      }
    }

    var urlParams = new URLSearchParams(window.location.search)

    if (urlParams.get('plan')) {
      setSelectedPlan(urlParams.get('plan'))
      scrollToPlans = true
    }

    if (scrollToPlans) {
      window.scrollTo({ top: getTopCoords(plansRef.current), behavior: 'smooth' })
    }
  }, [])

  const updateUrlFragment = (tabTitle) => {
    if (!tabTitle) return
    setSelectedTab(tabTitle)
    const newHash = encodeURIComponent(tabTitle.replace(/ /, '-').toLowerCase())
    const newUrl = new URL(window.location)
    newUrl.hash = newHash
    window.history.replaceState({}, '', newUrl)
  }

  if (!props.plans || props.plans.length === 0) {
    return <></>
  } else {
    return (
      <>
        <Box ref={plansRef} className={classes.root}>
          <SearchContainer maxWidth="lg">
            <WhiteBox>
              <SearchBoxWrap>
                <SearchBox
                  multiple={true}
                  placeholder={props.searchPlaceholder}
                  countries={globalPlan.footprintCountries}
                  selectedCountries={selectedCountries}
                  setSelectedCountries={setSelectedCountries}
                  noResultsText={props.noResultsText}
                />
              </SearchBoxWrap>
              {props.currencies && props.currencies.length > 0 && <CurrencyDropdown />}
            </WhiteBox>

            {searchPlans.length === 0 && (
              <>
                <AppBar className={classes.appbar} position="static" color="default" elevation="0">
                  <Tabs
                    value={selectedTab}
                    onChange={setSelectedTab}
                    className={classes.tabs}
                    centered
                    aria-label="tabs plans"
                    indicatorColor="transparent"
                    textColor="secondary"
                  >
                    <Tab
                      value="local"
                      onClick={() => {
                        updateUrlFragment('local')
                      }}
                      className={classes.tab}
                      label={props.localTabText}
                    />
                    {props.regions && props.regions.length !== 0 && (
                      <Tab
                        value="regional"
                        onClick={() => {
                          updateUrlFragment('regional')
                        }}
                        className={classes.tab}
                        label={props.regionalTabText}
                      />
                    )}

                    <Tab
                      value="global"
                      onClick={() => {
                        updateUrlFragment('global')
                      }}
                      className={classes.tab}
                      label={props.globalTabText}
                    />
                  </Tabs>
                </AppBar>
                {selectedTab === 'local' && (
                  <div className={classes.regionalLinkScroll}>
                    <Box className={classes.regionalLinks} display="flex">
                      {props.regions && props.regions.length > 0 && props.allRegionsText && (
                        <Link
                          underline="none"
                          key={props.allRegionsText}
                          className={
                            selectedRegion === null
                              ? classes.regionLink + ' ' + classes.selectedRegion
                              : classes.regionLink
                          }
                          href="#"
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            setSelectedRegion(null)
                          }}
                        >
                          {props.allRegionsText}
                        </Link>
                      )}

                      {props.regions &&
                        props.regions.map((region, i) => {
                          const linkStyle = clsx({
                            [classes.regionLink]: true,
                            [classes.selectedRegion]: selectedRegion === region
                          })
                          return (
                            <Link
                              underline="none"
                              key={region + i}
                              className={linkStyle}
                              href="#"
                              onClick={(e) => {
                                e.preventDefault()
                                e.stopPropagation()
                                setSelectedRegion(region)
                              }}
                            >
                              {region}
                            </Link>
                          )
                        })}
                    </Box>
                  </div>
                )}
              </>
            )}
          </SearchContainer>
        </Box>

        <Box className={classes.container}>
          {searchPlans.length > 0 &&
            searchPlans.map((plan, i) => {
              return (
                <SearchResult
                  key={`search-result-${i}`}
                  culture={props.culture}
                  allowOpenClose={searchPlans.length > 1}
                  selectedCurrency={localeContext.currency}
                  coverageTitle={props.coverageTitle}
                  coverageDescription={props.coverageDescription}
                  selectedCountries={selectedCountries}
                  howMuchDataLink={props.howMuchDataLink}
                  dataText={props.dataText}
                  modal={props.modal}
                  modalTitle={props.modalTitle}
                  appStoreImage={props.appStoreImage}
                  appStoreUrl={props.appStoreUrl}
                  playStoreImage={props.playStoreImage}
                  playStoreUrl={props.playStoreUrl}
                  freePlanEnabled={props.freePlanEnabled}
                  freePlanAllowance={props.freePlanAllowance}
                  freePlanPrice={props.freePlanPrice}
                  freePlanDuration={props.freePlanDuration}
                  freePlanUnit={props.freePlanUnit}
                  {...plan}
                />
              )
            })}

          {searchPlans.length === 0 && selectedTab === 'local' && localPlans && (
            <PlanGroup
              culture={props.culture}
              selectedPlan={selectedPlan}
              selectedCurrency={localeContext.currency}
              coverageTitle={props.coverageTitle}
              coverageDescription={props.coverageDescription}
              plans={localPlans}
              howMuchDataLink={props.howMuchDataLink}
              showMoreText={props.showMoreText}
              dataText={props.dataText}
              modal={props.modal}
              modalTitle={props.modalTitle}
              appStoreImage={props.appStoreImage}
              appStoreUrl={props.appStoreUrl}
              playStoreImage={props.playStoreImage}
              playStoreUrl={props.playStoreUrl}
              freePlanEnabled={props.freePlanEnabled}
              freePlanAllowance={props.freePlanAllowance}
              freePlanPrice={props.freePlanPrice}
              freePlanDuration={props.freePlanDuration}
              freePlanUnit={props.freePlanUnit}
            />
          )}

          {props.regions &&
            searchPlans.length === 0 &&
            selectedTab === 'regional' &&
            regionalPlans && (
              <PlanGroup
                culture={props.culture}
                selectedCurrency={localeContext.currency}
                selectedPlan={selectedPlan}
                coverageTitle={props.coverageTitle}
                coverageDescription={props.coverageDescription}
                plans={regionalPlans}
                showMoreText={props.showMoreText}
                howMuchDataLink={props.howMuchDataLink}
                lastItemOpen={true}
                dataText={props.dataText}
                modal={props.modal}
                freePlanEnabled={props.freePlanEnabled}
                freePlanAllowance={props.freePlanAllowance}
                freePlanPrice={props.freePlanPrice}
                freePlanDuration={props.freePlanDuration}
                freePlanUnit={props.freePlanUnit}
              />
            )}

          {searchPlans.length === 0 && selectedTab === 'global' && globalPlan && (
            <PlanGroup
              culture={props.culture}
              selectedCurrency={localeContext.currency}
              coverageTitle={props.coverageTitle}
              coverageDescription={props.coverageDescription}
              plans={[globalPlan]}
              showMoreText={props.showMoreText}
              howMuchDataLink={props.howMuchDataLink}
              dataText={props.dataText}
              lastItemOpen={true}
              modal={props.modal}
              modalTitle={props.modalTitle}
              appStoreImage={props.appStoreImage}
              appStoreUrl={props.appStoreUrl}
              playStoreImage={props.playStoreImage}
              playStoreUrl={props.playStoreUrl}
              planIcon="global"
              freePlanEnabled={props.freePlanEnabled}
              freePlanAllowance={props.freePlanAllowance}
              freePlanPrice={props.freePlanPrice}
              freePlanDuration={props.freePlanDuration}
              freePlanUnit={props.freePlanUnit}
            />
          )}
        </Box>
      </>
    )
  }
}

PlanTabs.propTypes = {
  plans: PropTypes.arrayOf(
    PropTypes.shape({
      allowances: PropTypes.array,
      footprintCountries: PropTypes.array,
      footprintType: PropTypes.string,
      iconName: PropTypes.string,
      iso: PropTypes.string,
      name: PropTypes.string,
      region: PropTypes.string
    })
  ),
  regions: PropTypes.array
}

PlanTabs.defaultProps = {
  plans: [],
  regions: []
}
